import styled from 'styled-components';

interface ButtonProps {
    variant?: 'primary' | 'secondary' | 'accent';
    size?: 'small' | 'medium' | 'large';
    fullWidth?: boolean;
}

/**
 * Button component with theme-based styling
 *
 * @example
 * ```tsx
 * <Button>Default Button</Button>
 * <Button variant="secondary" size="large">Large Secondary Button</Button>
 * <Button variant="accent" fullWidth>Full Width Accent Button</Button>
 * ```
 */
const Button = styled.button<ButtonProps>`
    display: inline-block;
    background-color: ${({ theme, variant = 'primary' }) =>
        variant === 'primary'
            ? theme.colors.primary
            : variant === 'secondary'
            ? theme.colors.secondary
            : theme.colors.accent};
    color: #ffffff;
    font-size: ${({ theme, size = 'medium' }) =>
        size === 'small'
            ? theme.fontSizes.small
            : size === 'large'
            ? theme.fontSizes.large
            : theme.fontSizes.medium};
    padding: ${({ theme, size = 'medium' }) =>
        size === 'small'
            ? `${theme.spacing.xs} ${theme.spacing.sm}`
            : size === 'large'
            ? `${theme.spacing.md} ${theme.spacing.xl}`
            : `${theme.spacing.sm} ${theme.spacing.lg}`};
    border: none;
    border-radius: ${({ theme }) => theme.borderRadius.medium};
    cursor: pointer;
    transition: background-color 0.2s ease-in-out, transform 0.1s ease;
    width: ${({ fullWidth }) => (fullWidth ? '100%' : 'auto')};
    font-weight: 500;

    &:hover {
        opacity: 0.9;
        transform: translateY(-1px);
    }

    &:active {
        transform: translateY(1px);
    }

    &:disabled {
        background-color: ${({ theme }) => theme.colors.textMuted};
        color: ${({ theme }) => theme.colors.backgroundDark};
        cursor: not-allowed;
        opacity: 0.5;
        transform: none;
    }
`;

export default Button;
